import styles from './base-catalog-dialog.module.scss';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook, useGetRequestNewHook } from '../requestUtils';
import { Col, Row } from 'reactstrap';
import { Autocomplete, Checkbox, CheckboxLabel, Dialog, DialogActions, DialogContent, DialogTitle, Froala, GrayOutlinedButton, Icon, Link, ListItemText, LoadingButton, MenuItem, Select, SnackBarContext, TextField, ToggleSecondaryButton } from '@frontend-workspace/react-shared-lib';
import { ICatalogItem } from '../_models/catalog-item';
import { ICategory } from '../_models/category';
import { IDuration } from '../_models/duration';
import { IEmailTemplateSurvey } from '../_models/email-template-survey';
import { ILevel } from '../_models/level';
import { IPrice } from '../_models/price';
import { IProductGroups } from '../_models/product-groups';
import { ISurveyTypes } from '../_models/survey-types';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface BaseCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  refreshData: () => void;
  catalogItem: ICatalogItem;
  handleOkText: string;
  headerText: string;
}

export function BaseCatalogDialog(props: BaseCatalogDialogProps) {
  //const { data: productGroups }: { data: IProductGroups[] } = useCourseGetRequestHook("settings/productGroups", []);
  const { data: productGroups }: { data: IProductGroups[] } = useGetRequestNewHook("relationProducts", [])
  const { data: images }: { data: string[] } = useCourseGetRequestHook("images", []);
  const { data: emailTemplatesSurveys }: { data: IEmailTemplateSurvey[] } = useCourseGetRequestHook("settings/emailTemplates/surveys", []);
  const { data: catalogCategories }: { data: ICategory[] } = useCourseGetRequestHook("resource/categories", []);
  const { data: levels }: { data: ILevel[] } = useCourseGetRequestHook("resource/levels", []);
  const { data: durations }: { data: IDuration[] } = useCourseGetRequestHook("resource/durations", []);
  const { data: prices }: { data: IPrice[] } = useCourseGetRequestHook("resource/prices", []);
  const { data: surveyTypes }: { data: ISurveyTypes[] } = useCourseGetRequestHook("catalog/surveyTypes", []);
  const { catalogItem } = props;

  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const [courseName, setCourseName] = useState<string>('');
  const [link, setLink] = useState<string>('');
  const [productIds, setProductIds] = useState<string[]>([]);
  const [iconUrl, setIconUrl] = useState<string>('');
  const [serveyEmailTemplateId, setServeyEmailTemplateId] = useState<string>('');
  const [doNotPublish, setDoNotPublish] = useState<boolean>(false);
  const [htmlContentIngress, setHtmlContentIngress] = useState<string>('');
  const [htmlContentDescription, setHtmlContentDescription] = useState<string>('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [levelId, setLevelId] = useState<string>('');
  const [durationId, setDurationId] = useState<string>('');
  const [priceId, setPriceId] = useState<string>('');
  const [evaluationId, setEvaluationId] = useState<string>('');

  const handleIconUrlChange = (event: ChangeEvent<HTMLSelectElement>) => setIconUrl(event.target.value);
  const handleServeyEmailTemplateIdChange = (event: ChangeEvent<HTMLSelectElement>) => setServeyEmailTemplateId(event.target.value);
  const handleDoNotPublishChange = (event: ChangeEvent<Element>, checked: boolean) => setDoNotPublish(checked);
  const handleLevelIdChange = (event: ChangeEvent<HTMLSelectElement>) => setLevelId(event.target.value);
  const handleDurationIdChange = (event: ChangeEvent<HTMLSelectElement>) => setDurationId(event.target.value);
  const handlePriceIdChange = (event: ChangeEvent<HTMLSelectElement>) => setPriceId(event.target.value);
  const handleEvaluationIdChange = (event: ChangeEvent<HTMLSelectElement>) => setEvaluationId(event.target.value);

  useEffect(() => {
    if (catalogItem) {
      setCourseName(catalogItem.courseName);
      setLink(catalogItem.link);
      setProductIds(catalogItem.relationProductIds || []);
      setIconUrl(catalogItem.iconUrl);
      setServeyEmailTemplateId(catalogItem.surveyEmailTemplateId ?? "");
      setDoNotPublish(catalogItem.doNotPublish);
      setHtmlContentIngress(catalogItem.courseIngress);
      setHtmlContentDescription(catalogItem.courseDescription);
      setCategoryIds(catalogItem.categories.map(c => c.id));
      setLevelId(catalogItem.level?.id ?? "");
      setDurationId(catalogItem.duration?.id ?? "");
      setPriceId(catalogItem.price?.id ?? "");
      setEvaluationId(catalogItem.surveyid ?? "");
    }
  }, [catalogItem]);

  const handleCourseNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const courseName = event.target.value;
    const link = courseName.split(' ').join('-');
    setLink(link.toLowerCase());
    setCourseName(courseName);
  };

  const handleCategoryChange = (categoryId: string) => {
    if (categoryIds?.includes(categoryId)) {
      setCategoryIds(categoryIds.filter(id => id !== categoryId));
    } else {
      setCategoryIds([...categoryIds, categoryId]);
    }
  };

  const handleProductChange = (productId: string, checked: boolean) => {
    if (checked) setProductIds([...productIds, productId]);
    else setProductIds(productIds.filter(id => id !== productId));
  };

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      courseName &&
      // (productIds && productIds.length > 0) &&
      serveyEmailTemplateId &&
      // (categoryIds && categoryIds.length > 0) &&
      levelId &&
      durationId &&
      priceId
    return requiredFieldsFilled ? false : true;
  };

  const handleUpdateCatalog = () => {
    setLoading(true);
    const body = {
      id: catalogItem.id,
      createdAt: catalogItem.createdAt,
      partNumber: catalogItem.partNumber,
      adskCourseId: catalogItem.adskCourseId,
      courseName: courseName,
      courseIngress: htmlContentIngress,
      courseDescription: htmlContentDescription,
      keywords: catalogItem.keywords,
      categories: catalogCategories.filter(category => categoryIds?.includes(category.id)),
      level: levels.find(l => l.id === levelId),
      duration: durations.find(d => d.id === durationId),
      price: prices.find(p => p.id === priceId),
      link: link,
      iconUrl: iconUrl,
      doNotPublish: doNotPublish,
      surveyid: evaluationId,
      surveyEmailTemplateId: serveyEmailTemplateId,
      relationProductIds: productIds,
      isDeleted: catalogItem.isDeleted
    }
    makeCourseRequest(`catalog/upsert`, 'POST', body).then(result => {
      setSnackbarMessage({ content: t("baseCatalog.updatedCourseInCatalog"), type: "info", });
      setLoading(false);
      props.refreshData();
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t("baseCatalog.couldNotUpdateCourseInCatalog"), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        <div dangerouslySetInnerHTML={{ __html: props.headerText }}></div>
      </DialogTitle>
      <DialogContent>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={<span>{t("standard.courseName")} &#42;</span>}
                name='courseName'
                fullWidth
                value={courseName || ''}
                onChange={handleCourseNameChange}
              />
            </div>
          </Col>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={t("baseCatalog.linkToCourseCatalog")}
                name='catalogLink'
                fullWidth
                value={link || ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setLink(event.target.value.toLowerCase())}
                disabled
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='mb-3'>
              <Autocomplete
                value={productIds || []}
                multiple
                options={productGroups}
                getOptionLabel={(productIds: any) => {
                  const product = productGroups.find(ua => ua.id === productIds);
                  return product?.productName || '';
                }}
                filterOptions={(options: IProductGroups[], state: any) => {
                  return options.filter(option => option.productName.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                }}
                renderOption={(props: any, productGroups: IProductGroups | null) => {
                  if (!productGroups) {
                    return null;
                  }
                  return (
                    <div onClick={(event) => handleProductChange(productGroups.id, !productIds.includes(productGroups.id))}>
                      <MenuItem key={productGroups.id} value={productGroups.id}>
                        <Checkbox
                          checked={productIds.includes(productGroups.id)}
                          onChange={(event: any, checked: boolean) => handleProductChange(productGroups.id, checked)}
                        />
                        <ListItemText primary={productGroups.productName} />
                      </MenuItem>
                    </div>
                  )
                }}
                renderInput={(params: any) => {
                  return <TextField
                    {...params}
                    fullWidth
                    label={<span>{t("standard.selectProducts")}</span>}
                  />
                }}
                onChange={(event: any, productIds: any) => setProductIds(productIds)}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <div className='d-flex align-items-center mb-3'>
              <div className='me-3'>
                <Select
                  label={t("baseCatalog.icon")}
                  name='icon'
                  displayEmpty
                  fullWidth
                  value={iconUrl || ''}
                  sx={{ width: 64 }}
                  onChange={handleIconUrlChange}>
                  {images.map(p =>
                    <MenuItem key={p} value={p}>
                      <img
                        src={p}
                        alt=""
                        width={iconUrl === p ? "20" : "32"}
                        height={iconUrl === p ? "20" : "32"}
                      />
                    </MenuItem>
                  )}
                </Select>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={12}>
            <div className='d-flex align-items-end flex-wrap mb-3'>
              <div className='me-3'>
                <Select
                  label={<span>{t("standard.evaluation")} &#42;</span>}
                  name='evaluation'
                  displayEmpty
                  fullWidth
                  value={evaluationId || ''}
                  onChange={handleEvaluationIdChange}
                  sx={{ minWidth: 300 }}>
                  <MenuItem value={''}>{t("standard.noEvaluation")}</MenuItem>
                  {surveyTypes.map((s) =>
                    <MenuItem
                      key={s.id}
                      value={s.id}>
                      {s.data.surveyinfo.title}
                    </MenuItem>
                  )}
                </Select>
              </div>
              <div>
                <Link
                  href="https://createSurvey.azurewebsites.net/"
                  target="_blank"
                  underline='always'
                  className='MuiTypography-body5'>
                  <div className='d-flex align-items-end'>
                    <Icon className='me-1'>open_in_new</Icon>{t("standard.addNewEvaluationForm")}
                  </div>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xl={3}>
            <div className='mb-3'>
              <Select
                label={<span>{t("standard.email")} &#42;</span>}
                displayEmpty
                fullWidth
                sx={{ minWidth: 300 }}
                name='serveyEmailTemplate'
                value={serveyEmailTemplateId || ''}
                onChange={handleServeyEmailTemplateIdChange}>
                {emailTemplatesSurveys.map(e => (
                  <MenuItem
                    key={e.id}
                    value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
        <div className='d-flex mb-3'>
          <CheckboxLabel
            label={t("standard.doNotPublish")}
            checked={doNotPublish || false}
            onChange={handleDoNotPublishChange}
          />
        </div>
        <div className='mb-3'>
          <label className='mb-1'>{t("baseCatalog.preamble")}</label>
          <Froala
            maxHeight={800}
            model={htmlContentIngress || ''}
            onModelChanged={setHtmlContentIngress}
          />
        </div>
        <div className='mb-3'>
          <label className='mb-1'>{t("standard.description")}</label>
          <Froala
            maxHeight={1000}
            model={htmlContentDescription || ''}
            onModelChanged={setHtmlContentDescription}
          />
        </div>
        <div className="d-flex flex-wrap">
          <div className='mb-3'>
            <label className='mb-1'>{<span>{t("baseCatalog.categories")}</span>}</label>
            <div>
              {catalogCategories.map(c =>
                <ToggleSecondaryButton
                  key={c.id}
                  value={c.id}
                  onChange={() => handleCategoryChange(c.id)}
                  size="small"
                  rounded={true}
                  selected={categoryIds?.includes(c.id)}
                  className="minButtonWidth me-3 my-2">
                  {c.name}
                </ToggleSecondaryButton>
              )}
            </div>
          </div>
        </div>
        <Row>
          <Col xl={4}>
            <Select
              label={<span>{t("baseCatalog.selectLevel")} &#42;</span>}
              name='level'
              displayEmpty
              fullWidth
              sx={{ minWidth: 300 }}
              value={levelId || ''}
              onChange={handleLevelIdChange}>
              {levels.map(l => (
                <MenuItem
                  key={l.id}
                  value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>
          </Col>
          <Col xl={4}>
            <Select
              label={<span>{t("baseCatalog.selectDuration")} &#42;</span>}
              name='duration'
              displayEmpty
              fullWidth
              sx={{ minWidth: 300 }}
              value={durationId || ''}
              onChange={handleDurationIdChange}>
              {durations.map(d => (
                <MenuItem
                  key={d.id}
                  value={d.id}>
                  {d.value} {d.unit}
                </MenuItem>
              ))}
            </Select>
          </Col>
          <Col xl={4}>
            <Select
              label={<span>{t("standard.selectPrice")} &#42;</span>}
              name='price'
              displayEmpty
              fullWidth
              sx={{ minWidth: 300 }}
              onChange={handlePriceIdChange}
              value={priceId || ''}>
              {prices.map(p => (
                <MenuItem
                  key={p.id}
                  value={p.id}>
                  {p.value}
                </MenuItem>
              ))}
            </Select>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleUpdateCatalog}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : props.handleOkText}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default BaseCatalogDialog;
